/**
 * Phone number formatting utilities for consistent US phone format
 * Format: (XXX) XXX-XXXX
 */

/**
 * Formats a phone number as user types: (XXX) XXX-XXXX
 * Only handles US 10-digit phone numbers
 */
export const formatPhoneNumber = (value: string): string => {
  let digits = value.replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('1')) {
    digits = digits.slice(1);
  }
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
};

/**
 * Strips formatting and returns only digits for storage/comparison
 */
export const stripPhoneFormatting = (value: string): string => {
  return value.replace(/\D/g, '');
};

/**
 * Validates a phone number has exactly 10 digits
 */
export const isValidPhoneNumber = (value: string): boolean => {
  let digits = value.replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('1')) {
    digits = digits.slice(1);
  }
  return digits.length === 10;
};

/**
 * Given a digit-only phone search string (7-11 digits), returns common
 * stored format variants so ilike filters can match phones saved as
 * "(262) 237-1323", "262-237-1323", "262.237.1323", "262 237 1323", or "2622371323".
 * Returns [] if the input is not a plausible phone fragment.
 */
export const generatePhoneSearchVariants = (digits: string): string[] => {
  const d = digits.replace(/\D/g, '');
  if (d.length < 7) return [];
  const variants = new Set<string>([d]);
  // 10-digit full-number formats
  if (d.length === 10) {
    variants.add(`(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`);
    variants.add(`${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6)}`);
    variants.add(`${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6)}`);
    variants.add(`${d.slice(0, 3)} ${d.slice(3, 6)} ${d.slice(6)}`);
  } else if (d.length === 11 && d.startsWith('1')) {
    const t = d.slice(1);
    variants.add(t);
    variants.add(`(${t.slice(0, 3)}) ${t.slice(3, 6)}-${t.slice(6)}`);
    variants.add(`${t.slice(0, 3)}-${t.slice(3, 6)}-${t.slice(6)}`);
  } else if (d.length === 7) {
    variants.add(`${d.slice(0, 3)}-${d.slice(3)}`);
    variants.add(`${d.slice(0, 3)}.${d.slice(3)}`);
  }
  return Array.from(variants);
};
